import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Typography,
    Button,
    Box,
    makeStyles,
    Paper,
    LinearProgress
} from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import { useStateValue } from '../../context/state/provider';
import { WIZARD_STEPS } from '../../constants/wizardSteps.constants';
import { createFirstAgent } from '../../services/api';
import { firestore } from '../../services/firebase/firebase';

const useStyles = makeStyles((theme) => ({
    stepContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        minHeight: 400,
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center'
    },
    timelineContainer: {
        width: '90%',
        maxWidth: '550px',
        margin: '0 auto',
        position: 'relative'
    },
    title: {
        marginBottom: theme.spacing(3),
        textAlign: 'center',
        color: theme.palette.primary.main,
        fontWeight: 600,
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.25rem',
            marginBottom: theme.spacing(2)
        }
    },
    description: {
        marginBottom: theme.spacing(4),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        [theme.breakpoints.down('sm')]: {
            marginBottom: theme.spacing(3),
            fontSize: '0.875rem'
        }
    },
    // Progress bar styles
    progressContainer: {
        width: '100%',
        maxWidth: '550px',
        margin: '0 auto',
        marginBottom: theme.spacing(3),
        padding: theme.spacing(0, 2)
    },
    progressBar: {
        height: 8,
        borderRadius: 4,
        backgroundColor: '#f0f0f0',
        '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            backgroundColor: theme.palette.primary.main
        }
    },
    progressText: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2)
    },
    progressPercentage: {
        fontWeight: 600,
        color: theme.palette.primary.main,
        fontSize: '1rem'
    },
    progressMessage: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        fontStyle: 'italic'
    },
    phaseIndicator: {
        textAlign: 'center',
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1),
        backgroundColor: '#f8f9fa',
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.palette.primary.main}20`
    },

    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: theme.spacing(4),
        gap: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            width: '100%',
            maxWidth: '300px'
        }
    },
    backButton: {
        minWidth: 120,
        [theme.breakpoints.down('sm')]: {
            width: '100%'
        }
    },
    helpButton: {
        minWidth: 180,
        [theme.breakpoints.down('sm')]: {
            width: '100%'
        }
    },
    // Timeline styles
    timelineItem: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2.5),
        '&:last-child': {
            marginBottom: 0
        },
        [theme.breakpoints.down('sm')]: {
            marginBottom: theme.spacing(2)
        }
    },
    circleWrapper: {
        position: 'relative',
        flexShrink: 0,
        marginRight: theme.spacing(2.5),
        [theme.breakpoints.down('sm')]: {
            marginRight: theme.spacing(2)
        }
    },
    circle: {
        width: 14,
        height: 14,
        borderRadius: '50%',
        position: 'relative',
        transition: 'all 0.3s ease',
        [theme.breakpoints.down('sm')]: {
            width: 12,
            height: 12
        }
    },
    circleCompleted: {
        backgroundColor: '#1642f3'
    },
    circleCurrent: {
        backgroundColor: '#1642f3',
        animation: '$pulse 2s infinite'
    },
    circleInactive: {
        backgroundColor: '#ccc'
    },
    line: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: 2,
        transform: 'translateX(-50%)',
        transformOrigin: 'top',
        height: 0,
        transition: 'height 0.3s ease, background-color 0.3s ease',
        [theme.breakpoints.down('sm')]: {
            width: 1
        }
    },
    lineCompleted: {
        backgroundColor: '#1642f3'
    },
    lineCurrent: {
        backgroundColor: '#1642f3'
    },
    lineInactive: {
        backgroundColor: '#ccc'
    },
    messageText: {
        flexGrow: 1,
        margin: 0,
        fontSize: 18,
        lineHeight: 1.5,
        backgroundColor: '#fff',
        border: '1px solid #ddd',
        borderRadius: theme.spacing(0.5),
        padding: theme.spacing(1.875),
        transition: 'all 0.3s ease',
        [theme.breakpoints.down('sm')]: {
            fontSize: 16,
            padding: theme.spacing(1.5)
        },
        [theme.breakpoints.down('xs')]: {
            fontSize: 14,
            padding: theme.spacing(1.25)
        }
    },
    messageTextCompleted: {
        color: '#333',
        borderColor: '#1642f3'
    },
    messageTextCurrent: {
        color: '#333',
        borderColor: '#1642f3',
        fontWeight: 600
    },
    messageTextInactive: {
        color: '#ccc',
        borderColor: '#ddd'
    },
    // Gear animation styles
    gearsContainer: {
        marginTop: theme.spacing(6),
        width: '100%',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: 0,
        paddingBottom: theme.spacing(3)
    },
    gearLeft: {
        width: 40,
        height: 40,
        animation: '$rotateClockwise 4s linear infinite',
        marginBottom: '4px',
        marginRight: '-13px',
        '& svg': {
            width: '100%',
            height: '100%'
        }
    },
    gearCenter: {
        width: 60,
        height: 60,
        animation: '$rotateCounterClockwise 4s linear infinite',
        marginBottom: '0px',
        marginRight: '-10px',
        '& svg': {
            width: '100%',
            height: '100%'
        }
    },
    gearRight: {
        width: 50,
        height: 50,
        animation: '$rotateClockwise 4s linear infinite',
        marginBottom: '-4px',
        marginLeft: '-6px',
        '& svg': {
            width: '100%',
            height: '100%'
        }
    },
    // Animations
    '@keyframes pulse': {
        '0%': {
            transform: 'scale(0.9)',
            boxShadow: '0 0 0 0 rgba(22, 66, 243, 0.7)'
        },
        '70%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 10px rgba(22, 66, 243, 0)'
        },
        '100%': {
            transform: 'scale(0.9)',
            boxShadow: '0 0 0 0 rgba(22, 66, 243, 0)'
        }
    },
    '@keyframes rotateClockwise': {
        from: {
            transform: 'rotate(0deg)'
        },
        to: {
            transform: 'rotate(360deg)'
        }
    },
    '@keyframes rotateCounterClockwise': {
        from: {
            transform: 'rotate(0deg)'
        },
        to: {
            transform: 'rotate(-360deg)'
        }
    }
}));



// Gear SVG Component
const GearIcon = () => (
    <svg viewBox="0 0 24 24" fill="#234fe2">
        <path d="M19.14,12.94 C19.14,12.63 19.14,12.32 19.14,12 
         C19.14,11.68 19.14,11.37 19.14,11.06 L21.7,9.11 
         C21.89,8.97 21.95,8.7 21.83,8.52 L19.83,5.48 
         C19.75,5.34 19.61,5.28 19.47,5.31 L16.53,6.24 
         C15.95,5.82 15.33,5.47 14.66,5.21 L14.1,2.27 
         C14.07,2.12 13.93,2 13.78,2 L10.23,2 C10.08,2 9.94,2.12 9.9,2.27 
         L9.34,5.21 C8.67,5.47 8.06,5.82 7.47,6.24 L4.53,5.31 
         C4.39,5.28 4.25,5.34 4.17,5.48 L2.17,8.52 C2.05,8.7 2.11,8.97 2.3,9.11 
         L4.86,11.06 C4.86,11.37 4.86,11.68 4.86,12 
         C4.86,12.32 4.86,12.63 4.86,12.94 L2.3,14.89 
         C2.11,15.03 2.05,15.3 2.17,15.48 L4.17,18.52 
         C4.25,18.66 4.39,18.72 4.53,18.69 L7.47,17.76 
         C8.06,18.18 8.67,18.53 9.34,18.79 L9.9,21.73 
         C9.94,21.88 10.08,22 10.23,22 L13.78,22 
         C13.93,22 14.07,21.88 14.1,21.73 
         L14.66,18.79 C15.33,18.53 15.95,18.18 16.53,17.76 
         L19.47,18.69 C19.61,18.72 19.75,18.66 19.83,18.52 
         L21.83,15.48 C21.95,15.3 21.89,15.03 21.7,14.89 
         L19.14,12.94 Z 
         M12,15.5 C10.07,15.5 8.5,13.93 8.5,12 C8.5,10.07 10.07,8.5 12,8.5 
         C13.93,8.5 15.5,10.07 15.5,12 C15.5,13.93 13.93,15.5 12,15.5 Z"/>
    </svg>
);

export default function CreationStep({ checkTokenExpiration }) {
    const classes = useStyles();
    const [{ wizardData, user }, dispatch] = useStateValue();
    
    // Legacy timeline state (still used for fallback)
    const [messageIndex, setMessageIndex] = useState(0);
    const [isCreating, setIsCreating] = useState(false);
    const [creationComplete, setCreationComplete] = useState(false);
    const [error, setError] = useState('');
    
    // New progress state for Firestore integration
    const [progress, setProgress] = useState(0);
    const [currentPhase, setCurrentPhase] = useState('initializing');
    const [progressMessage, setProgressMessage] = useState('Preparing your agent...');
    const [progressId, setProgressId] = useState('');
    const [useRealTimeProgress, setUseRealTimeProgress] = useState(false);
    
    const circleRefs = useRef([]);
    const lineRefs = useRef([]);
    const progressListenerRef = useRef(null);

    // Enhanced timeline messages that sync with progress percentage
    const timelineSteps = useMemo(() => [
        {
            threshold: 0,
            message: "I am creating your first agent, it will take a few seconds. Please do not close this page."
        },
        {
            threshold: 25,
            message: "I am uploading and processing your documents securely."
        },
        {
            threshold: 45,
            message: "I am creating your agent's knowledge base and skills."
        },
        {
            threshold: 65,
            message: "I am feeding the knowledge into your first agent and analyzing your content."
        },
        {
            threshold: 95,
            message: "🎉 Your agent is ready! Preparing for testing..."
        }
    ], []);

    // Legacy messages removed - now using consistent timelineSteps only

    // Last message variations removed - using consistent timeline only

    const [messages, setMessages] = useState([]);

    // Initialize messages when arrays are available - ALWAYS use timeline steps for consistency
    useEffect(() => {
        if (timelineSteps.length > 0) {
            // Always use enhanced timeline messages for consistency
            const timelineMessages = timelineSteps.map(step => step.message);
            setMessages(timelineMessages);
            console.log('[TIMELINE] Using consistent timeline messages:', timelineMessages);
        }
    }, [timelineSteps]); // Removed other dependencies to prevent switching

    // Calculate line heights between circles
    useEffect(() => {
        if (circleRefs.current.length > 1) {
            circleRefs.current.forEach((circleRef, index) => {
                if (circleRef && circleRef.current && index < circleRefs.current.length - 1) {
                    const nextCircleRef = circleRefs.current[index + 1];
                    if (nextCircleRef && nextCircleRef.current) {
                        const circleRect = circleRef.current.getBoundingClientRect();
                        const nextCircleRect = nextCircleRef.current.getBoundingClientRect();

                        const circleCenterY = circleRect.top + circleRect.height / 2;
                        const nextCircleCenterY = nextCircleRect.top + nextCircleRect.height / 2;
                        const height = nextCircleCenterY - circleCenterY;

                        const lineRef = lineRefs.current[index];
                        if (lineRef && lineRef.current) {
                            lineRef.current.style.height = height > 0 ? `${height}px` : '0px';
                        }
                    }
                }
            });
        }
    }, [messageIndex, messages]);

    // Timeline animation effect - DISABLED in favor of consistent timeline
    useEffect(() => {
        if (isCreating && !useRealTimeProgress) {
            console.log('[TIMELINE] Fallback animation disabled - using consistent timeline only');
            // Don't run the old animation - just use the consistent timelineSteps
            // This prevents switching between different message sets
        }
        // Old animation code removed to prevent timeline inconsistency
    }, [isCreating, useRealTimeProgress]);

    // Setup Firestore listener for real-time progress
    useEffect(() => {
        if (!progressId || !useRealTimeProgress) return;

        console.log('[PROGRESS] Setting up Firestore listener for:', progressId);
        
        const unsubscribe = firestore
            .collection('agentCreationProgress')
            .doc(progressId)
            .onSnapshot(
                (doc) => {
                    if (doc.exists) {
                        const data = doc.data();
                        console.log('[PROGRESS] Received update:', data);
                        
                        const newProgress = data.percentage || 0;
                        setProgress(newProgress);
                        setCurrentPhase(data.phase || 'initializing');
                        setProgressMessage(data.message || 'Processing...');
                        
                        // Update timeline based on progress percentage (no timers!)
                        // Find the correct step based on progress, ensuring we don't go backwards
                        let currentStepIndex = 0;
                        for (let i = timelineSteps.length - 1; i >= 0; i--) {
                            if (newProgress >= timelineSteps[i].threshold) {
                                currentStepIndex = i;
                                break;
                            }
                        }
                        
                        // Only advance steps, never go backwards, but ensure we hit all steps
                        if (currentStepIndex > messageIndex) {
                            console.log(`[PROGRESS] Advancing from step ${messageIndex + 1} to step ${currentStepIndex + 1} at ${newProgress}%`);
                            
                            // If jumping multiple steps, advance through each one with a small delay
                            if (currentStepIndex - messageIndex > 1) {
                                console.log(`[PROGRESS] Jumping multiple steps, advancing through each one`);
                                let nextStep = messageIndex + 1;
                                const advanceStep = () => {
                                    if (nextStep <= currentStepIndex) {
                                        setMessageIndex(nextStep);
                                        nextStep++;
                                        if (nextStep <= currentStepIndex) {
                                            setTimeout(advanceStep, 500); // 500ms delay between steps
                                        }
                                    }
                                };
                                advanceStep();
                            } else {
                                setMessageIndex(currentStepIndex);
                            }
                            // Don't update messages array - keep consistent timeline
                        } else if (currentStepIndex < messageIndex) {
                            console.log(`[PROGRESS] Progress ${newProgress}% suggests step ${currentStepIndex + 1}, but staying at step ${messageIndex + 1} (no backwards)`);
                        }
                        
                        // Handle completion - but don't auto-advance, let main success logic handle it
                        if (data.phase === 'complete') {
                            console.log('[PROGRESS] Agent creation complete - progress listener received completion');
                            // Don't call setCreationComplete here - let the main API response handle it
                            // This prevents conflicts between progress updates and API response handling
                        }
                        
                        // Handle errors - but don't show error UI for partial failures
                        if (data.phase === 'error') {
                            console.log('[PROGRESS] Error phase detected, but not showing error UI (letting main API response handle partial failures)');
                            // Don't call setError() here for partial failures
                            // The main API response will handle the proper success/partial failure display
                            // This prevents the confusing error flash before success screen
                        }
                    }
                },
                (error) => {
                    console.error('[PROGRESS] Firestore listener error:', error);
                    // Fall back to legacy progress if Firestore fails
                    setUseRealTimeProgress(false);
                }
            );
        
        progressListenerRef.current = unsubscribe;
        
        return () => {
            if (unsubscribe) {
                console.log('[PROGRESS] Cleaning up Firestore listener');
                unsubscribe();
            }
        };
    }, [progressId, useRealTimeProgress, dispatch, timelineSteps, messageIndex]);

    const handleCreateAgent = useCallback(async () => {
        setIsCreating(true);
        setError('');

        try {
                    // Generate unique progress ID
        const newProgressId = `progress_${user?.uid || 'anonymous'}_${Date.now()}`;
        setProgressId(newProgressId);
        setUseRealTimeProgress(true);
        
        // Initialize progress state
        setProgress(0);
        setMessageIndex(0); // Start at first step
        setCurrentPhase('initializing');
        
        // Messages are already initialized by useEffect with timelineSteps
        console.log('[CREATE_AGENT] Starting agent creation with timeline messages already set');

            // Create FormData for the API call
            const formData = new FormData();
            const cleanChatbotName = wizardData.chatbotName.trim().replace(/\s+/g, " ");
            const cleanSkillName = cleanChatbotName.trim().replace(/\s+/g, '').slice(0, 96) + "-skillContent";
            const cleanSkillDescription = wizardData.skillDescription.trim().replace(/\s+/g, " ");

            formData.append('chatbotName', cleanChatbotName);
            formData.append('skillName', cleanSkillName);
            formData.append('skillDescription', cleanSkillDescription);
            formData.append('user', user?.uid || 'anonymous');
            
            // Add admin panel integration data
            if (wizardData.clientId) {
                formData.append('clientId', wizardData.clientId);
            }
            if (wizardData.prefilledFromAdmin) {
                formData.append('prefilledFromAdmin', 'true');
            }

            // Append files
            wizardData.files.forEach((file, index) => {
                formData.append(`file${index}`, file);
            });

            console.log('Creating agent with data:', {
                chatbotName: cleanChatbotName,
                skillName: cleanSkillName,
                skillDescription: cleanSkillDescription,
                filesCount: wizardData.files.length,
                progressId: newProgressId
            });

            // Call API with progress tracking
            // No need for artificial delay since we have real-time progress
            const response = await createFirstAgent(formData, newProgressId, wizardData.clientId);

            console.log('Agent creation response:', response.data);
            console.log('[DEBUG] HTTP Status:', response.status);
            console.log('[DEBUG] Response statusCode:', response.data.statusCode);
            console.log('[DEBUG] Partial failure?', response.data.data?.partialFailure);
            console.log('[DEBUG] Failed documents:', response.data.data?.failedDocuments);

            if (response.data.statusCode === 200) {
                console.log('[DEBUG] Entering SUCCESS path');
                const message = response.data && response.data.message;
                const data = response.data.data;
                console.log('[DEBUG] Success data:', data);
                
                if (message && message.includes("already exists")) {
                    setError("An agent with this name already exists. Please try a different name.");
                    setIsCreating(false);
                    return;
                }

                // Success - store the chatbot ID and proceed
                console.log('[DEBUG] About to dispatch UPDATE_WIZARD_DATA');
                dispatch({
                    type: 'UPDATE_WIZARD_DATA',
                    data: { 
                        chatbotId: data.chatbotId,
                        skillId: data.skillId || 'default-skill',
                        // Store partial failure information for display
                        partialFailure: data.partialFailure || false,
                        failedDocuments: data.failedDocuments || [],
                        successfulDocuments: data.successfulDocuments || 0,
                        totalDocuments: data.totalDocuments || 0
                    }
                });

                console.log('[DEBUG] About to setCreationComplete(true)');
                setCreationComplete(true);
                
                // DON'T auto-advance if there are partial failures - let user read the warnings
                if (!data.partialFailure) {
                    // Auto-advance immediately for complete success
                    dispatch({
                        type: 'SET_CURRENT_STEP',
                        step: WIZARD_STEPS.CHATBOT_TEST
                    });
                }
                // For partial failures, user must click button to proceed

            } else {
                throw new Error(response.data.message || 'Failed to create agent');
            }

        } catch (err) {
            console.error('[DEBUG] CATCH BLOCK TRIGGERED - Error creating agent:', err);
            console.log('[DEBUG] Full error response:', err.response);
            console.log('[DEBUG] Error response data:', err.response?.data);
            
            const errorMessage = err.response?.data?.message || err.message;
            const isUserFriendly = err.response?.data?.userFriendly || false;
            const statusCode = err.response?.status;
            
            console.log('[DEBUG] Error details:', { errorMessage, isUserFriendly, statusCode });
            
            let displayError = '';
            
            if (errorMessage && errorMessage.includes("already exists")) {
                displayError = "An agent with this name already exists. Please go back and try a different name.";
            } else if (errorMessage && errorMessage.includes("Maximum number of wizard agents reached")) {
                displayError = "Maximum number of wizard agents reached for this subscription.";
            } else if (errorMessage && errorMessage.startsWith("RATE_LIMITED:")) {
                // Parse rate limit error: "RATE_LIMITED:60:Too many requests..."
                const parts = errorMessage.split(':');
                // const minutes = parts[1] || '60'; // Not used in display
                const message = parts.slice(2).join(':') || 'Too many requests. Please try again later.';
                displayError = `Rate Limited: ${message}`;
            } else if (errorMessage && (errorMessage.includes("Too many requests") || errorMessage.includes("Rate Limited"))) {
                displayError = "Too many requests from your IP. Please try again in 60 minutes.";
            } else if (isUserFriendly && errorMessage) {
                // Use the specific user-friendly error message from backend
                displayError = errorMessage;
            } else {
                displayError = "Failed to create the agent. Please try again or contact support.";
            }
            
            setError(displayError);
            setIsCreating(false);
            setUseRealTimeProgress(false); // Fall back to error display
        }
    }, [wizardData, user, dispatch]); // timelineSteps is stable from useMemo

    // Auto-start creation when component mounts (only once)
    useEffect(() => {
        if (!isCreating && !creationComplete && !error) {
            const timer = setTimeout(() => {
                handleCreateAgent();
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // ^ Intentionally empty to prevent infinite re-creation loops

    const handleBack = () => {
        dispatch({
            type: 'SET_CURRENT_STEP',
            step: WIZARD_STEPS.NAME_YOUR_AGENT
        });
    };

    const handleRequestHelp = () => {
        // TODO: Implement help request functionality
        console.log('Request help clicked');
    };

    if (creationComplete) {
        const hasPartialFailure = wizardData.partialFailure;
        const failedDocs = wizardData.failedDocuments || [];
        const successfulDocs = wizardData.successfulDocuments || 0;
        
        return (
            <Box className={classes.stepContainer}>
                <Typography variant="h5" className={classes.title} style={{ color: '#4CAF50' }}>
                    🎉 Agent Created Successfully!
                </Typography>
                <Typography variant="body1" className={classes.description}>
                    Your agent "{wizardData.chatbotName}" has been created and is ready for testing.
                </Typography>
                
                {hasPartialFailure && (
                    <Box style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffeaa7' }}>
                        <Typography variant="body2" style={{ color: '#856404', fontWeight: 'bold', marginBottom: '8px' }}>
                            ⚠️ Some files were skipped:
                        </Typography>
                        <Typography variant="body2" style={{ color: '#856404', marginBottom: '8px' }}>
                            Successfully processed {successfulDocs} file{successfulDocs !== 1 ? 's' : ''}, 
                            but {failedDocs.length} file{failedDocs.length !== 1 ? 's' : ''} could not be processed:
                        </Typography>
                        {failedDocs.map((failedDoc, index) => (
                            <Typography key={index} variant="body2" style={{ color: '#856404', marginLeft: '8px', marginBottom: '4px' }}>
                                • <strong>{failedDoc.failedDocument}</strong>: {failedDoc.message}
                            </Typography>
                        ))}
                        <Typography variant="body2" style={{ color: '#856404', marginTop: '8px', fontStyle: 'italic' }}>
                            Your agent is still functional with the successfully processed files.
                        </Typography>
                    </Box>
                )}
                
                {hasPartialFailure ? (
                    <Box style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={() => {
                                // Check token expiration before proceeding (if token exists)
                                if (checkTokenExpiration && !checkTokenExpiration()) {
                                    // Token expired, checkTokenExpiration handles the error display
                                    return;
                                }

                                dispatch({
                                    type: 'SET_CURRENT_STEP',
                                    step: WIZARD_STEPS.CHATBOT_TEST
                                });
                            }}
                            style={{ minWidth: '200px' }}
                        >
                            Test Your Agent
                        </Button>
                    </Box>
                ) : (
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: '16px' }}>
                        Redirecting to test your agent...
                    </Typography>
                )}
            </Box>
        );
    }

    if (error) {
        return (
            <Box className={classes.stepContainer}>
                <Typography variant="h6" className={classes.title} style={{ color: '#f44336' }}>
                    Creation Failed
                </Typography>
                <Typography variant="body1" className={classes.description} style={{ color: '#f44336' }}>
                    {error}
                </Typography>
                <Box className={classes.buttonContainer}>
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        className={classes.backButton}
                        startIcon={<ArrowBack />}
                    >
                        Go Back
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleRequestHelp}
                        className={classes.helpButton}
                    >
                        Need Help?
                    </Button>
                </Box>
            </Box>
        );
    }

    // Get phase display name
    const getPhaseDisplayName = (phase) => {
        const phaseNames = {
            'initializing': 'Initializing',
            'setup': 'Setting Up',
            'upload': 'Uploading Files',
            'skill': 'Creating Skill',
            'processing': 'Analyzing Documents',
            'finalization': 'Finalizing',
            'complete': 'Complete',
            'error': 'Error'
        };
        return phaseNames[phase] || 'Processing';
    };



    return (
        <Box className={classes.stepContainer}>
            {/* Progress Bar Section - Only show when using real-time progress */}
            {useRealTimeProgress && isCreating && (
                <Box className={classes.progressContainer}>
                    <Box className={classes.phaseIndicator}>
                        <Typography variant="body2" color="primary">
                            Current Phase: {getPhaseDisplayName(currentPhase)}
                        </Typography>
                    </Box>
                    
                    <LinearProgress 
                        variant="determinate" 
                        value={progress} 
                        className={classes.progressBar}
                    />
                    
                    <Box className={classes.progressText}>
                        <Typography variant="body2" className={classes.progressMessage}>
                            {progressMessage}
                        </Typography>
                        <Typography variant="body2" className={classes.progressPercentage}>
                            {Math.round(progress)}%
                        </Typography>
                    </Box>
                </Box>
            )}

            <Box className={classes.timelineContainer}>
                    <Box>
                        {messages.map((message, index) => {
                        const isCompleted = index < messageIndex;
                        const isCurrent = index === messageIndex;
                        const isLast = index === messages.length - 1;

                        // Create refs for circles and lines
                        if (!circleRefs.current[index]) {
                            circleRefs.current[index] = React.createRef();
                        }
                        if (!lineRefs.current[index]) {
                            lineRefs.current[index] = React.createRef();
                        }

                        // Determine classes for circle
                        let circleClass = classes.circle;
                        if (isCompleted) {
                            circleClass += ` ${classes.circleCompleted}`;
                        } else if (isCurrent) {
                            circleClass += ` ${classes.circleCurrent}`;
                        } else {
                            circleClass += ` ${classes.circleInactive}`;
                        }

                        // Determine classes for line
                        let lineClass = classes.line;
                        if (isCompleted) {
                            lineClass += ` ${classes.lineCompleted}`;
                        } else if (isCurrent) {
                            lineClass += ` ${classes.lineCurrent}`;
                        } else {
                            lineClass += ` ${classes.lineInactive}`;
                        }

                        // Determine classes for message text
                        let messageClass = classes.messageText;
                        if (isCompleted) {
                            messageClass += ` ${classes.messageTextCompleted}`;
                        } else if (isCurrent) {
                            messageClass += ` ${classes.messageTextCurrent}`;
                        } else {
                            messageClass += ` ${classes.messageTextInactive}`;
                        }

                        return (
                            <Box key={index} className={classes.timelineItem}>
                                <Box className={classes.circleWrapper}>
                                    <Box
                                        ref={circleRefs.current[index]}
                                        className={circleClass}
                                    />
                                    {!isLast && (
                                        <Box
                                            ref={lineRefs.current[index]}
                                            className={lineClass}
                                        />
                                    )}
                                </Box>
                                <Paper
                                    className={messageClass}
                                    elevation={0}
                                >
                                    <Typography>
                                        {message}
                                    </Typography>
                                </Paper>
                            </Box>
                        );
                    })}
                </Box>

                {/* Animated gears at the bottom */}
                {isCreating && (
                    <Box className={classes.gearsContainer}>
                        <Box className={classes.gearLeft}>
                            <GearIcon />
                        </Box>
                        <Box className={classes.gearCenter}>
                            <GearIcon />
                        </Box>
                        <Box className={classes.gearRight}>
                            <GearIcon />
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
